import { useOne, useTranslation, useUpdate } from "@refinedev/core";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { get } from "lodash";
import { Breadcrumbs } from "components/breadcrumbs";
import { FullScreenLoading } from "components/fullScreenLoading";
import { getConfig } from "../config";
import { PickupEventForm } from "../form";
import { getEventLabels } from "../labels";
import {
  PickupEventDetail,
  PickupEventFormValues,
  PickupEventVariant,
} from "../types";
import { getMessage } from "../utils";

export const PickupEventEditPage = ({ variant }: { variant: PickupEventVariant }) => {
  const config = getConfig(variant);
  const { translate, getLocale } = useTranslation();
  const language = getLocale();
  const labels = getEventLabels(variant, translate);
  const { id } = useParams();
  const navigate = useNavigate();
  const { mutateAsync, isLoading: isUpdating } = useUpdate();

  const {
    data,
    isFetching,
    isPreviousData,
    isLoading,
  } = useOne<PickupEventDetail>({
    resource: config.detailResource,
    id,
    meta: {
      resource: "data",
    },
    queryOptions: {
      enabled: !!id,
    } as any,
  });

  const eventDetail = data?.data ?? null;

  const submit = async (values: PickupEventFormValues) => {
    if (!id) {
      toast.error("Event id not found");
      return;
    }

    try {
      const res = await mutateAsync({
        resource: config.updateResource,
        id,
        meta: {
          ignoreResourceWrapper: true,
        },
        invalidates: ["list", "many", "detail"],
        values,
      });

      toast.success(getMessage(get(res, "data.message"), language, labels.editSuccess));
      navigate(config.detailPath(id));
    } catch (error) {
      // Refine handles update error notifications.
    }
  };

  if (isLoading) return <FullScreenLoading />;

  return (
    <div className={variant === "wps" ? "w-full" : "wrapper"}>
      <Breadcrumbs
        type={variant === "wps" ? "wps" : undefined}
        customPageLabel={variant === "admin" ? labels.editTitle : undefined}
        breadcrumbItems={
          variant === "wps"
            ? [
                { label: labels.checkIn },
                { label: labels.pageTitle, href: config.basePath },
                { label: labels.editTitle, href: `${config.basePath}/:id/edit` },
              ]
            : undefined
        }
        disableBorder={variant === "wps" ? true : undefined}
      />

      {eventDetail ? (
        <PickupEventForm
          backPath={config.detailPath(id ?? "")}
          defaultData={eventDetail}
          onSubmit={submit}
          variant={variant}
          key={eventDetail.id}
        />
      ) : null}

      {(isFetching && isPreviousData) || isUpdating ? <FullScreenLoading /> : null}
    </div>
  );
};
